import React from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { cn } from '../../lib/utils';

interface FormFieldProps {
  label: string;
  name: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'number' | 'textarea' | 'select';
  value: string | number | undefined | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | string[];
  touched?: boolean;
  success?: boolean;
  helperText?: string;
  options?: Array<{ value: string | number; label: string }>;
  rows?: number;
  className?: string;
  inputClassName?: string;
  showPasswordToggle?: boolean;
  autoComplete?: string;
  min?: number;
  max?: number;
  step?: number;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onBlur,
  placeholder,
  required = false,
  disabled = false,
  error,
  touched = false,
  success = false,
  helperText,
  options = [],
  rows = 3,
  className = '',
  inputClassName = '',
  showPasswordToggle = false,
  autoComplete,
  min,
  max,
  step,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  // Normalize error to array for consistent handling
  const errors = React.useMemo(() => {
    if (!error) return [];
    return Array.isArray(error) ? error : [error];
  }, [error]);

  const hasError = touched && errors.length > 0;
  const hasSuccess = touched && !hasError && success && value;

  const inputType = type === 'password' && showPasswordToggle
    ? (showPassword ? 'text' : 'password')
    : type;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setIsFocused(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const baseInputClasses = cn(
    'w-full px-3 py-2 text-sm border rounded-lg transition-all duration-200',
    'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-1',
    {
      // Default state
      'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20': !hasError && !hasSuccess,
      // Error state
      'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50': hasError,
      // Success state
      'border-green-300 focus:border-green-500 focus:ring-green-500/20 bg-green-50': hasSuccess,
      // Disabled state
      'bg-gray-100 cursor-not-allowed opacity-60': disabled,
      // Focused state
      'ring-2': isFocused,
    },
    inputClassName
  );

  const renderInput = () => {
    // Ensure value is always a string to prevent controlled/uncontrolled input issues
    const safeValue = value == null ? '' : String(value);
    
    const commonProps = {
      id: name,
      name,
      value: safeValue,
      onChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      placeholder,
      required,
      disabled,
      autoComplete,
      className: baseInputClasses,
    };

    switch (type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={rows}
          />
        );

      case 'select':
        return (
          <select {...commonProps}>
            <option value="">{placeholder || `Select ${label.toLowerCase()}`}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'number':
        return (
          <input
            {...commonProps}
            type="number"
            min={min}
            max={max}
            step={step}
          />
        );

      default:
        return (
          <input
            {...commonProps}
            type={inputType}
          />
        );
    }
  };

  return (
    <div className={cn('space-y-1', className)}>
      {/* Label */}
      <label 
        htmlFor={name}
        className={cn(
          'block text-sm font-medium transition-colors',
          {
            'text-gray-700': !hasError && !hasSuccess,
            'text-red-700': hasError,
            'text-green-700': hasSuccess,
          }
        )}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Input Container */}
      <div className="relative">
        {renderInput()}

        {/* Password Toggle */}
        {type === 'password' && showPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        )}

        {/* Status Icons */}
        {(hasError || hasSuccess) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {hasError && (
              <AlertCircle className="w-4 h-4 text-red-500" />
            )}
            {hasSuccess && (
              <CheckCircle className="w-4 h-4 text-green-500" />
            )}
          </div>
        )}
      </div>

      {/* Helper Text */}
      {helperText && !hasError && (
        <p className="text-xs text-gray-500 mt-1">
          {helperText}
        </p>
      )}

      {/* Error Messages */}
      {hasError && (
        <div className="space-y-1 mt-1">
          {errors.map((err, index) => (
            <p 
              key={index}
              className="text-xs text-red-600 flex items-start gap-1"
              role="alert"
              aria-live="polite"
            >
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{err}</span>
            </p>
          ))}
        </div>
      )}

      {/* Success Message */}
      {hasSuccess && !helperText && (
        <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
          <CheckCircle className="w-3 h-3" />
          <span>Valid</span>
        </p>
      )}
    </div>
  );
};

export default FormField;